import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { CONFIG } from '@frontend/config';
import {
  CatalogEntry,
  CircuitCatalogFilters,
  CircuitsSearchPayload,
  ConnectorInfo,
  isConnectorNotConfigured,
  useCircuitCatalog,
  useConnectorCapabilities,
  useSearchCircuits,
} from '../queries';
import {
  resolveCircuitDepartureAvailability,
  unsearchableCircuitProviders,
} from '../circuitDepartureAvailability';
import {
  cleanParams,
  enumParam,
  isIncompleteSearch,
  listParam,
  numParam,
  serializeList,
  UrlParams,
  useSearchUrlState,
} from '../useSearchUrlState';
import {
  CatalogFilters,
  CatalogSort,
  CircuitFilters,
  CircuitOffer,
  CircuitSort,
  DURATION_BANDS,
  EMPTY_CATALOG_FILTERS,
  EMPTY_CIRCUIT_FILTERS,
  catalogCurrencies,
  catalogFacetCount,
  circuitCatalogCitiesFilterFromParams,
  circuitCatalogQueryFromParams,
  circuitDuration,
  circuitPlaceCitiesFromParams,
  circuitPlaceCityToParam,
  circuitProductFromUrlParams,
  circuitProductUrlParams,
  circuitSearchPayloadFromParams,
  departureFacetCount,
  departureMonth,
  filterCatalog,
  filterDepartures,
  formatDate,
  monthLabel,
  seasonOf,
  seriesOf,
  sortCatalog,
  sortDeparturesBy,
} from '../circuits';
import { committedParty } from '../committedBookingContext';
import { PlaceRef } from '../places';
import { CircuitProductHeader } from './CircuitProductHeader';
import { CircuitProductInput } from './CircuitProductInput';
import { CircuitDestinationsPlaceInput } from './CircuitDestinationsPlaceInput';
import { CircuitCatalogResults } from './CircuitCatalogResults';
import { describeCircuits } from '../describeSearch';
import { SearchBar, SearchDivider, SearchField } from './SearchBar';
import {
  SEARCH_DATE_FIELD_WIDTH,
  SEARCH_FIELD_HALF_SHARE,
  SEARCH_PASSENGER_FIELD_WIDTH,
} from '../searchFieldLayout';
import {
  FilterChip,
  FilterGroup,
  ResultFiltersBar,
} from './ResultFiltersBar';
import { RecentSearches } from './RecentSearches';
import { CircuitDepartures } from './CircuitDepartures';
import { CircuitProductDialog } from './CircuitProductDialog';
import { formatMoney } from '../money';
import { BookingModal } from './BookingModal';
import { quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import { useFareCountdown } from '../useFareCountdown';
import { useSearchResultAuthority } from '../useSearchResultAuthority';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import {
  latestSearchDate,
  reconcileSearchEndDate,
  todaySearchDate,
} from '../searchDates';
import { SearchDatePicker } from './SearchDatePicker';
import {
  COUNT_ONLY_PASSENGER_RULES,
  countOnlyPassengers,
} from '../searchPassengers';
import { SearchPassengerPicker } from './SearchPassengerPicker';
import { releaseNumberInputOnWheel } from '../numberInput';
import { CircuitOccupancyPicker } from './CircuitOccupancyPicker';
import {
  circuitOccupancyCount,
  circuitOccupancyFromParam,
  circuitOccupancyLimitsFor,
  circuitOccupancyParam,
  circuitOccupancyValid,
  defaultCircuitOccupancy,
} from '../circuitOccupancy';

/**
 * Buscador de circuitos. Es el único vertical con DOS pantallas de resultado,
 * porque son dos preguntas distintas:
 *
 * 1. **Descubrir** (catálogo rastreado): qué circuitos existen que pasen por
 *    estas ciudades y duren esto. No cotiza: sin precio vendible ni provenance.
 * 2. **Cotizar** (búsqueda dirigida): elegido el circuito, qué SALIDAS tiene en
 *    una ventana de fechas, con precio del proveedor en vivo.
 *
 * Conviven en la misma barra a propósito — un asesor que ya sabe qué circuito
 * quiere no tiene por qué pasar por el descubrimiento — pero los resultados se
 * ven distintos, porque uno se puede reservar y el otro no.
 *
 * El precio del proveedor es POR PERSONA y el backend ya lo totaliza por los
 * pax buscados; acá se muestran las dos cifras para que quede explícito.
 */
function payloadFromParams(
  params: Record<string, string>,
): CircuitsSearchPayload | null {
  // La forma del payload (y que el proveedor viaje con el ref) es una sola
  // definición, en `circuits.ts`: acá solo se leen los params de la URL.
  return circuitSearchPayloadFromParams({
    buscar: params.buscar,
    circuito: params.circuito,
    circuitoProveedor: params.circuitoProveedor,
    desde: params.desde,
    hasta: params.hasta,
    pax: numParam(params, 'pax', 2, { min: 1, max: 9 }),
    occupancy: circuitOccupancyFromParam(params.ocupacion),
  });
}

/**
 * Ciudades canónicas del filtro. Los ids `plc_*` viajan opacos al hub.
 */
/**
 * Filtros del multibuscador, o `null` si no hay ninguno. Sin filtros no se
 * consulta: sería pedir el catálogo entero para mostrar las primeras 200
 * fichas de nada en particular.
 */
function catalogFromParams(
  params: Record<string, string>,
): CircuitCatalogFilters | null {
  const minDays = numParam(params, 'dmin', 0, { min: 0, max: 365 });
  const maxDays = numParam(params, 'dmax', 0, { min: 0, max: 365 });
  return circuitCatalogQueryFromParams({
    buscar: params.buscar,
    circuito: params.circuito,
    ciudades: params.ciudades,
    minDays,
    maxDays,
  });
}

export function CircuitsSearchForm() {
  const { t, i18n } = useTranslation();
  const url = useSearchUrlState({ vertical: 'circuits', dateParam: 'desde' });
  const searchPayload = useMemo(
    () => payloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchCircuits(searchPayload);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload
    ? quoteSearchIdentity('circuits', CONFIG.AGENCY_ID, searchPayload)
    : undefined;
  const catalogQuery = useMemo(() => catalogFromParams(url.params), [url.params]);
  const invalidCommittedCities =
    url.params.buscar === '1' &&
    !url.params.circuito &&
    circuitCatalogCitiesFilterFromParams(url.params.ciudades).status === 'invalid';
  const catalog = useCircuitCatalog(catalogQuery);
  // Quién puede COTIZAR salidas en esta agencia. Sale de la MISMA autoridad
  // que ya usa el picker de destinos (`GET /rutero/connectors`): el frontend
  // no decide qué conectores existen, los lee (21.1 aplicado a datos).
  const {
    data: connectors,
    isSuccess: connectorsLoaded,
    isFetching: connectorsFetching,
  } = useConnectorCapabilities();
  const connectorAuthority = useMemo(
    () => ({ isSuccess: connectorsLoaded, isFetching: connectorsFetching }),
    [connectorsLoaded, connectorsFetching],
  );
  /**
   * El proveedor que ACABA de contestar la búsqueda dirigida en pantalla: el
   * resultado autoritativo le gana al snapshot cacheado de conectores (21.23),
   * así que sobre ese proveedor no se avisa nada.
   */
  const provenSearchableProvider = search.data
    ? searchPayload?.providers?.[0]
    : undefined;
  const departureAvailability = useMemo(
    () => (provider?: string) =>
      resolveCircuitDepartureAvailability({
        connectors: connectors as ConnectorInfo[] | undefined,
        authority: connectorAuthority,
        provider,
        provenSearchable: provenSearchableProvider,
      }),
    [connectors, connectorAuthority, provenSearchableProvider],
  );

  const [product, setProduct] = useState<CatalogEntry | null>(() =>
    circuitProductFromUrlParams(url.params));
  const [citiesPlaces, setCitiesPlaces] = useState<PlaceRef[]>(() =>
    circuitPlaceCitiesFromParams(url.params.ciudades ?? ''));
  const [minDays, setMinDays] = useState(() =>
    numParam(url.params, 'dmin', 0, { min: 0, max: 365 }));
  const [maxDays, setMaxDays] = useState(() =>
    numParam(url.params, 'dmax', 0, { min: 0, max: 365 }));
  const [includePastSeasons, setIncludePastSeasons] = useState(false);
  const [from, setFrom] = useState(() => url.params.desde ?? '');
  const [to, setTo] = useState(() => url.params.hasta ?? '');
  const [toDateOpen, setToDateOpen] = useState(false);
  const [pax, setPax] = useState(() => numParam(url.params, 'pax', 2, { min: 1, max: 9 }));
  const [occupancy, setOccupancy] = useState(() =>
    circuitOccupancyFromParam(url.params.ocupacion));
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<CircuitOffer> | null
  >(null);
  const bookingOffer = bookingQuote?.offer ?? null;
  // Dos juegos de filtros porque hay dos listas: la grilla de descubrimiento y
  // las salidas de un circuito ya elegido. No responden lo mismo.
  const [catalogFilters, setCatalogFilters] = useState<CatalogFilters>(() => ({
    ...EMPTY_CATALOG_FILTERS,
    providers: listParam(url.params, 'fProveedor'),
    durations: listParam(url.params, 'fDuracion'),
  }));
  const [catalogSort, setCatalogSort] = useState<CatalogSort>(() =>
    enumParam(url.params, 'ordenCat', ['name', 'price', 'duration'] as const, 'price'));
  const [filters, setFilters] = useState<CircuitFilters>(() => ({
    ...EMPTY_CIRCUIT_FILTERS,
    categories: listParam(url.params, 'fCategoria'),
    months: listParam(url.params, 'fMes'),
  }));
  const [sort, setSort] = useState<CircuitSort>(() =>
    enumParam(url.params, 'orden', ['date', 'price'] as const, 'date'));

  // Los refinamientos viajan en la URL (sin tocar el historial): un link
  // compartido muestra la misma vista filtrada.
  useEffect(() => {
    url.update({
      fProveedor: serializeList(catalogFilters.providers),
      fDuracion: serializeList(catalogFilters.durations),
      ordenCat: catalogSort === 'price' ? undefined : catalogSort,
      fCategoria: serializeList(filters.categories),
      fMes: serializeList(filters.months),
      orden: sort === 'date' ? undefined : sort,
    });
  }, [catalogFilters, catalogSort, filters, sort, url]);
  /**
   * La ficha abierta. Guarda la ENTRADA del catálogo que la abrió, no solo el
   * ref: es el snapshot con el que se avanza a las salidas (21.23). Abierta
   * desde el encabezado de salidas no hay entrada — ya se está en las salidas.
   */
  const [sheet, setSheet] = useState<
    { productRef: string; provider?: string; entry?: CatalogEntry } | undefined
  >(undefined);
  /** El contexto reservable de la búsqueda dirigida sale de la URL. */
  const searchedPax = searchPayload?.occupancy
    ? circuitOccupancyCount(searchPayload.occupancy)
    : searchPayload?.pax ?? 2;
  const searchedProduct = searchPayload
    ? circuitProductFromUrlParams(url.params)
    : null;
  const draftOccupancyLimits = circuitOccupancyLimitsFor(
    connectors as ConnectorInfo[] | undefined,
    product?.provider,
  );
  const searchedOccupancyLimits = circuitOccupancyLimitsFor(
    connectors as ConnectorInfo[] | undefined,
    searchPayload?.providers?.[0],
  );

  useEffect(() => {
    if (!draftOccupancyLimits) return;
    if (circuitOccupancyValid(occupancy, draftOccupancyLimits)) return;
    setOccupancy(defaultCircuitOccupancy(draftOccupancyLimits, pax));
  }, [draftOccupancyLimits, occupancy, pax]);

  /**
   * Qué pregunta está haciendo el asesor. Elegir un circuito manda: si ya sabe
   * cuál quiere, no tiene sentido devolverle una grilla para descubrirlo.
   */
  const mode: 'quote' | 'discover' = product ? 'quote' : 'discover';
  const minFromDate = todaySearchDate();
  const minToDate = latestSearchDate(minFromDate, from);
  const dateWindowValid = Boolean(
    from && from >= minFromDate && (!to || to >= minToDate),
  );
  const canQuote = Boolean(
    product &&
    dateWindowValid &&
    draftOccupancyLimits &&
    circuitOccupancyValid(occupancy, draftOccupancyLimits),
  );
  const canDiscover = Boolean(citiesPlaces.length || minDays || maxDays);
  const canSearch = mode === 'quote' ? canQuote : canDiscover;

  /** El proveedor de la búsqueda EN CURSO: la URL confirmada es la autoridad. */
  const searchedProvider = searchPayload?.providers?.[0];
  /**
   * El del circuito elegido en el formulario, que puede ser OTRO —el asesor ya
   * cambió de circuito pero todavía no buscó—. Se mira primero: el estado se
   * dice antes del click, sobre lo que está por buscar.
   */
  const draftProvider = mode === 'quote' ? product?.provider : undefined;
  /** El hub rechazó ESTA búsqueda porque el proveedor no está configurado. */
  const searchRejectedForConnector =
    search.isError && isConnectorNotConfigured(search.error);
  /**
   * El proveedor del circuito no cotiza salidas en esta agencia. DOS fuentes,
   * un solo estado (21.18.1): las capacidades del conector —que lo dicen
   * antes de buscar— y el rechazo del hub (`CONNECTOR_NOT_CONFIGURED`), que
   * es el que queda cuando las capacidades todavía no cargaron o quedaron
   * viejas. Sin proveedor conocido (link viejo sin `circuitoProveedor`) queda
   * `undefined`: no se inventa un nombre, y el rechazo se cuenta como el
   * error genérico antes que como un aviso sin sujeto.
   */
  const unsearchableProvider = searchRejectedForConnector
    ? searchedProvider
    : [draftProvider, searchedProvider].find(
      (provider) => departureAvailability(provider) === 'unavailable',
    );

  /** Vacía la búsqueda: campos, URL y memoria de navegación. */
  const handleClear = () => {
    setProduct(null);
    setCatalogFilters(EMPTY_CATALOG_FILTERS);
    setFilters(EMPTY_CIRCUIT_FILTERS);
    setCitiesPlaces([]);
    setMinDays(0);
    setMaxDays(0);
    setFrom('');
    setTo('');
    setPax(2);
    setOccupancy(undefined);
    url.reset();
  };

  /**
   * Los filtros del descubrimiento, tal como viajan en la URL. `ciudades` es
   * el param canónico de pares `plc_*:nombre` (ver `circuits.ts`).
   */
  const discoverParams = {
    ciudades:
      citiesPlaces.map(circuitPlaceCityToParam).join(',') ||
      undefined,
    dmin: minDays ? String(minDays) : undefined,
    dmax: maxDays ? String(maxDays) : undefined,
  };

  /**
   * El formulario tal como se escribiría en la URL, en el modo en el que está.
   * Es la ÚNICA derivación del borrador: de acá salen la búsqueda que se
   * confirma y el payload contra el que se compara la comprometida (21.22).
   *
   * Descubrir NO cotiza: sus params limpian el circuito y la ventana de
   * salidas, así que soltar el circuito elegido sin volver a buscar deja en
   * pantalla salidas de otra consulta — y eso es divergir.
   */
  const formParams: UrlParams =
    mode === 'discover'
      ? {
        buscar: '1',
        ...discoverParams,
        ...circuitProductUrlParams(null),
      }
      : {
        buscar: '1',
        // Nombre y temporada no se buscan: repintan el autocomplete y el aviso
        // de temporada pasada; la serie es solo metadata del resumen reservado.
        ...circuitProductUrlParams(product),
        desde: from,
        hasta: to || undefined,
        pax: String(occupancy ? circuitOccupancyCount(occupancy) : pax),
        ocupacion: circuitOccupancyParam(occupancy),
        ...discoverParams,
      };
  const draftPayload = payloadFromParams(cleanParams(formParams));
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);

  const handleSearch = () => {
    if (!canSearch) return;
    // Los filtros son facetas del resultado ANTERIOR: arrastrarlos a una
    // búsqueda nueva esconde resultados por una razón que ya no está en
    // pantalla. Mismo criterio que autos y traslados.
    setCatalogFilters(EMPTY_CATALOG_FILTERS);
    setFilters(EMPTY_CIRCUIT_FILTERS);
    if (mode === 'discover') {
      // El catálogo NO lleva fechas ni pax: no cotiza. Se limpian para que un
      // link de descubrimiento no arrastre una ventana de salidas ajena.
      url.commit(formParams);
      return;
    }
    if (!product) return;
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('circuits', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  /**
   * Elegir un circuito de la grilla: pasa de descubrir a cotizar. Si todavía no
   * hay fecha desde, NO se busca — se deja el circuito puesto y el aviso de
   * abajo pide la fecha. Inventarle una fecha al asesor sería cotizar salidas
   * que no pidió.
   */
  /**
   * "Cambiar circuito": soltar el producto elegido y volver al DESCUBRIMIENTO,
   * conservando las ciudades y la duración con las que el asesor llegó hasta
   * acá. Es la vuelta atrás de `handlePickFromCatalog`, no un "limpiar todo":
   * perder los filtros obligaría a rearmar la búsqueda para mirar otra opción
   * de la misma lista.
   */
  const handleChangeCircuit = () => {
    setProduct(null);
    setFilters(EMPTY_CIRCUIT_FILTERS);
    url.commit({
      buscar: '1',
      ...circuitProductUrlParams(null),
      ...discoverParams,
    });
  };

  const handlePickFromCatalog = (entry: CatalogEntry) => {
    setProduct(entry);
    const entryLimits = circuitOccupancyLimitsFor(
      connectors as ConnectorInfo[] | undefined,
      entry.provider,
    );
    const entryOccupancy = entryLimits
      ? circuitOccupancyValid(occupancy, entryLimits)
        ? occupancy
        : defaultCircuitOccupancy(entryLimits, pax)
      : undefined;
    setOccupancy(entryOccupancy);
    if (!dateWindowValid) {
      url.update({
        ...circuitProductUrlParams(entry),
        buscar: undefined,
      });
      return;
    }
    url.commit({
      buscar: '1',
      ...circuitProductUrlParams(entry),
      desde: from,
      hasta: to || undefined,
      pax: String(entryOccupancy ? circuitOccupancyCount(entryOccupancy) : pax),
      ocupacion: circuitOccupancyParam(entryOccupancy),
      ...discoverParams,
    });
  };

  const allDepartures = useMemo(
    () => (search.data?.results ?? []) as CircuitOffer[],
    [search.data],
  );
  const departures = useMemo(
    () => sortDeparturesBy(filterDepartures(allDepartures, filters), sort),
    [allDepartures, filters, sort],
  );
  /** Categorías y meses que EXISTEN en lo cotizado: no se ofrecen filtros vacíos. */
  const categories = useMemo(
    () => [...new Set(allDepartures.map((offer) => offer.category ?? '').filter(Boolean))].sort(),
    [allDepartures],
  );
  const months = useMemo(
    () => [...new Set(allDepartures.map(departureMonth).filter(Boolean))].sort(),
    [allDepartures],
  );
  const departureFacet = (patch: Partial<CircuitFilters>) =>
    departureFacetCount(allDepartures, filters, patch);
  const activeDepartureFilters = filters.categories.length + filters.months.length;

  const allCatalog = useMemo(
    () => catalog.data?.results ?? [],
    [catalog.data],
  );
  const catalogResults = useMemo(
    () => sortCatalog(filterCatalog(allCatalog, catalogFilters), catalogSort),
    [allCatalog, catalogFilters, catalogSort],
  );
  const catalogProviders = useMemo(
    () => [...new Set(allCatalog.map((entry) => entry.provider ?? '').filter(Boolean))].sort(),
    [allCatalog],
  );
  /**
   * Los proveedores de la grilla que NO cotizan salidas: la card lo dice ahí
   * mismo, para que el asesor no descubra el callejón sin salida recién al
   * pedir las salidas del circuito que ya eligió.
   */
  const unsearchableCatalogProviders = useMemo(
    () =>
      unsearchableCircuitProviders({
        providers: catalogProviders,
        connectors: connectors as ConnectorInfo[] | undefined,
        authority: connectorAuthority,
        provenSearchable: provenSearchableProvider,
      }),
    [catalogProviders, connectors, connectorAuthority, provenSearchableProvider],
  );
  const catalogFacet = (patch: Partial<CatalogFilters>) =>
    catalogFacetCount(allCatalog, catalogFilters, patch);
  const activeCatalogFilters =
    catalogFilters.providers.length + catalogFilters.durations.length;
  /** Con dos monedas en la grilla, ordenar por precio compara lo incomparable. */
  const mixedCurrencies = catalogCurrencies(allCatalog).length > 1;

  const toggleInList = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  const bookingDetails = bookingOffer
    ? [
      `${bookingOffer.title ?? ''}${
        searchedProduct && seriesOf(searchedProduct)
          ? ` · ${seriesOf(searchedProduct)}`
          : ''
      }`,
      [
        t('travelServices.circuits.departureOn', {
          date: formatDate(bookingOffer.startDate),
        }),
        circuitDuration(bookingOffer)
          ? t('travelServices.durationDays', {
            count: circuitDuration(bookingOffer),
          })
          : '',
        `${t('travelServices.pax')}: ${searchedPax}`,
      ]
        .filter(Boolean)
        .join(' · '),
      bookingOffer.unitPrice
        ? t('travelServices.circuits.perPerson', {
          price: formatMoney(bookingOffer.unitPrice),
        })
        : '',
    ].filter(Boolean)
    : undefined;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
      >
        <SearchField label={t('travelServices.circuits.destinations')} grow width={260}>
          <CircuitDestinationsPlaceInput
            label={t('travelServices.circuits.destinations')}
            value={citiesPlaces}
            onChange={setCitiesPlaces}
            width="100%"
          />
        </SearchField>
        <SearchDivider />
        <SearchField label={t('travelServices.circuits.duration')} width={140}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <TextField
              size="small"
              type="number"
              label={t('travelServices.circuits.minDays')}
              value={minDays || ''}
              onChange={(event) =>
                setMinDays(Math.max(0, Math.min(365, Number(event.target.value) || 0)))
              }
              inputProps={{
                min: 0,
                max: 365,
                onWheel: releaseNumberInputOnWheel,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              –
            </Typography>
            <TextField
              size="small"
              type="number"
              label={t('travelServices.circuits.maxDays')}
              value={maxDays || ''}
              onChange={(event) =>
                setMaxDays(Math.max(0, Math.min(365, Number(event.target.value) || 0)))
              }
              inputProps={{
                min: 0,
                max: 365,
                onWheel: releaseNumberInputOnWheel,
              }}
            />
          </Box>
        </SearchField>
        <SearchDivider />
        <SearchField label={t('travelServices.circuits.product')} grow width={280}>
          <CircuitProductInput
            label={t('travelServices.circuits.product')}
            value={product}
            onChange={setProduct}
            includePastSeasons={includePastSeasons}
            width="100%"
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.circuits.from')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_HALF_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.circuits.from')}
            value={from}
            minDate={minFromDate}
            onChange={(nextDate) => {
              setFrom(nextDate);
              setTo((current) => reconcileSearchEndDate(nextDate, current));
            }}
            onAccept={() => {
              if (mode === 'quote') setToDateOpen(true);
            }}
            // Solo hace falta para COTIZAR: en el descubrimiento no se piden
            // salidas, y marcarlo en rojo seria pedir algo que no se usa.
            required={mode === 'quote'}
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.circuits.to')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_HALF_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.circuits.to')}
            value={to}
            onChange={setTo}
            minDate={minToDate}
            open={toDateOpen}
            onOpen={() => setToDateOpen(true)}
            onClose={() => setToDateOpen(false)}
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.passengerPicker.label')}
          width={SEARCH_PASSENGER_FIELD_WIDTH}
        >
          {product && draftOccupancyLimits && occupancy ? (
            <CircuitOccupancyPicker
              value={occupancy}
              limits={draftOccupancyLimits}
              onChange={(nextOccupancy) => {
                setOccupancy(nextOccupancy);
                setPax(circuitOccupancyCount(nextOccupancy));
              }}
            />
          ) : (
            <SearchPassengerPicker
              value={countOnlyPassengers(pax)}
              onChange={(nextPassengers) => setPax(nextPassengers.length)}
              rules={COUNT_ONLY_PASSENGER_RULES}
            />
          )}
        </SearchField>
      </SearchBar>

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="circuits"
          describe={(params) => describeCircuits(params, t, i18n.language)}
        />
      )}

      {/* El filtro de ciudades es AND: pedir de más no amplía, vacía. Se avisa
          recién con la segunda, que es cuando empieza a doler. */}
      {citiesPlaces.length > 1 && (
        <Alert severity="info">
          {t('travelServices.circuits.allCitiesNotice')}
        </Alert>
      )}

      {/* Fuera de la barra: no es un criterio de búsqueda sino qué se ofrece
          en el desplegable de circuitos. */}
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={includePastSeasons}
            onChange={(event) => setIncludePastSeasons(event.target.checked)}
          />
        }
        label={
          <Typography variant="body2">
            {t('travelServices.circuits.includePastSeasons')}
          </Typography>
        }
      />

      {/* Elegir un circuito de temporada pasada da una búsqueda vacía que
          parece un error: se avisa ANTES de buscar. */}
      {product && seasonOf(product) > 0 &&
        seasonOf(product) < new Date().getFullYear() && (
        <Alert severity="warning">
          {t('travelServices.circuits.pastSeasonWarning', {
            season: seasonOf(product),
          })}
        </Alert>
      )}

      {/* Se eligió un circuito de la grilla pero falta la fecha: sin ella no
          hay salidas que pedir. Se dice acá, donde el asesor está mirando. */}
      {product && !from && (
        <Alert severity="info">{t('travelServices.circuits.pickDate')}</Alert>
      )}

      {product && connectorsLoaded && !draftOccupancyLimits && (
        <Alert severity="warning">
          {t('travelServices.circuits.occupancy.unavailable')}
        </Alert>
      )}

      {/* El circuito es de un proveedor que esta agencia no puede cotizar. Es
          un estado del CONECTOR, no de la ventana de fechas: decirlo así es
          la diferencia entre "pedile a un admin las credenciales" y que el
          asesor pruebe fechas para siempre. */}
      {unsearchableProvider && (
        <Alert severity="warning">
          {t('travelServices.circuits.providerNotSearchable', {
            provider: unsearchableProvider,
          })}
        </Alert>
      )}

      {/* La URL pide una búsqueda que no se puede ejecutar (link
          recortado o editado a mano): decirlo, no quedarse mudo. Solo aplica
          al modo cotizar; el descubrimiento no tiene payload obligatorio. */}
      {url.params.circuito && isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describeCircuits(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {/* ── Descubrimiento ─────────────────────────────────────────────── */}
      {catalog.isLoading && <ResultsSkeleton archetype="product" />}
      {catalog.isError && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}
      {catalog.data && !catalog.isLoading && (
        <>
          {allCatalog.length > 0 && (
            <ResultFiltersBar
              activeCount={activeCatalogFilters}
              onClear={() => setCatalogFilters(EMPTY_CATALOG_FILTERS)}
              resultCount={catalogResults.length}
              sort={{
                value: catalogSort,
                onChange: (value) => setCatalogSort(value as CatalogSort),
                options: [
                  { value: 'price', label: t('travelServices.cars.sortPrice') },
                  { value: 'duration', label: t('travelServices.circuits.sortDuration') },
                  { value: 'name', label: t('travelServices.circuits.sortName') },
                ],
              }}
            >
              {(stacked) => (
                <>
                  <FilterGroup
                    label={t('travelServices.transfers.providerFilter')}
                    stacked={stacked}
                  >
                    {catalogProviders.map((provider) => (
                      <FilterChip
                        key={provider}
                        label={provider}
                        count={catalogFacet({ providers: [provider] })}
                        active={catalogFilters.providers.includes(provider)}
                        onClick={() =>
                          setCatalogFilters((prev) => ({
                            ...prev,
                            providers: toggleInList(prev.providers, provider),
                          }))
                        }
                      />
                    ))}
                  </FilterGroup>
                  <FilterGroup
                    label={t('travelServices.circuits.duration')}
                    stacked={stacked}
                  >
                    {DURATION_BANDS.map((band) => (
                      <FilterChip
                        key={band.key}
                        label={t(`travelServices.circuits.band.${band.key}`)}
                        count={catalogFacet({ durations: [band.key] })}
                        active={catalogFilters.durations.includes(band.key)}
                        onClick={() =>
                          setCatalogFilters((prev) => ({
                            ...prev,
                            durations: toggleInList(prev.durations, band.key),
                          }))
                        }
                      />
                    ))}
                  </FilterGroup>
                </>
              )}
            </ResultFiltersBar>
          )}

          {/* Ordenar por precio con dos monedas en pantalla compara números
              que no son comparables: se dice, no se disimula. */}
          {mixedCurrencies && catalogSort === 'price' && (
            <Alert severity="info">
              {t('travelServices.circuits.mixedCurrencies')}
            </Alert>
          )}

          <CircuitCatalogResults
            results={catalogResults}
            total={catalog.data.total}
            catalog={catalog.data.catalog}
            unsearchableProviders={unsearchableCatalogProviders}
            onSelect={(entry) =>
              setSheet({
                productRef: entry.id,
                provider: entry.provider,
                entry,
              })
            }
          />
        </>
      )}

      {search.isLoading && <ResultsSkeleton archetype="product" />}
      {/* El proveedor no configurado NO es una falla de búsqueda: ya se contó
          arriba con su propio estado, y repetirlo como error genérico haría
          exactamente lo que este aviso viene a evitar. Cualquier OTRA falla
          —incluso con un circuito no cotizable elegido en el formulario—
          sigue saliendo como error: un fallo que no se ve es peor. */}
      {search.isError && !(searchRejectedForConnector && unsearchableProvider) && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}
      <ProviderStatusStrip providers={search.data?.providers} />
      {invalidCommittedCities && (
        <Alert severity="warning">
          {t('travelServices.circuits.reselectCanonicalDestinations')}
        </Alert>
      )}
      {!search.data &&
        !search.isLoading &&
        !search.isError &&
        !unsearchableProvider &&
        !catalogQuery &&
        !invalidCommittedCities &&
        !catalog.isLoading && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.circuits.emptyState')}
        </Typography>
      )}

      {search.data && !search.isLoading && (
        <>
          {allDepartures.length > 0 && (
            <ResultFiltersBar
              activeCount={activeDepartureFilters}
              onClear={() => setFilters(EMPTY_CIRCUIT_FILTERS)}
              resultCount={departures.length}
              sort={{
                value: sort,
                onChange: (value) => setSort(value as CircuitSort),
                options: [
                  { value: 'date', label: t('travelServices.circuits.sortDate') },
                  { value: 'price', label: t('travelServices.cars.sortPrice') },
                ],
              }}
            >
              {(stacked) => (
                <>
                  {/* Solo si el operador publica categorías: con una sola,
                      el filtro no separa nada y es ruido. */}
                  {categories.length > 1 && (
                    <FilterGroup
                      label={t('travelServices.circuits.categoryFilter')}
                      stacked={stacked}
                    >
                      {categories.map((category) => (
                        <FilterChip
                          key={category}
                          label={category}
                          count={departureFacet({ categories: [category] })}
                          active={filters.categories.includes(category)}
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              categories: toggleInList(prev.categories, category),
                            }))
                          }
                        />
                      ))}
                    </FilterGroup>
                  )}
                  {months.length > 1 && (
                    <FilterGroup
                      label={t('travelServices.circuits.monthFilter')}
                      stacked={stacked}
                    >
                      {months.map((month) => (
                        <FilterChip
                          key={month}
                          label={monthLabel(month, i18n.language)}
                          count={departureFacet({ months: [month] })}
                          active={filters.months.includes(month)}
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              months: toggleInList(prev.months, month),
                            }))
                          }
                        />
                      ))}
                    </FilterGroup>
                  )}
                </>
              )}
            </ResultFiltersBar>
          )}

          <Typography variant="body2" color="text.secondary">
            {t('travelServices.circuits.departuresFound', {
              count: departures.length,
            })}
          </Typography>
          {departures.length === 0 ? (
            <Alert severity="info">{t('travelServices.circuits.noDepartures')}</Alert>
          ) : (
            <>
              {/* Todas las salidas son del MISMO circuito (la búsqueda es
                  dirigida): el producto va una vez arriba, no repetido en
                  cada card. */}
              <CircuitProductHeader
                offer={departures[0]}
                onOpenSheet={() => {
                  const ref = departures[0].productRef;
                  // El ref es provider-scoped: la oferta dice de quién es.
                  if (ref) {
                    setSheet({
                      productRef: ref,
                      provider: departures[0].provider,
                    });
                  }
                }}
                onChangeCircuit={handleChangeCircuit}
              />
              <CircuitDepartures
                departures={departures}
                searchedPax={searchedPax}
                canSelect={resultAuthority.canSelectResult}
                onBook={(offer) =>
                  setBookingQuote(
                    captureSelectableSearchQuote(
                      offer,
                      search.dataUpdatedAt,
                      'circuits',
                      CONFIG.AGENCY_ID,
                      searchPayload,
                      Date.now(),
                      formDiverged,
                    ),
                  )
                }
              />
            </>
          )}
        </>
      )}

      <CircuitProductDialog
        productRef={sheet?.productRef}
        provider={sheet?.provider}
        entry={sheet?.entry}
        unsearchableProvider={
          departureAvailability(sheet?.provider) === 'unavailable'
            ? sheet?.provider
            : undefined
        }
        onSeeDepartures={(entry) => {
          setSheet(undefined);
          handlePickFromCatalog(entry);
        }}
        onClose={() => setSheet(undefined)}
      />

      <BookingModal
        vertical="circuits"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        details={bookingDetails}
        party={committedParty({
          vertical: 'circuits',
          payload: searchPayload,
          occupancyLimits: searchedOccupancyLimits,
          ageBands: bookingOffer?.ageBands,
        })}
        initialOfferSelection={
          searchPayload?.occupancy ? { occupancy: searchPayload.occupancy } : undefined
        }
        occupancyLimits={searchedOccupancyLimits}
        onClose={() => setBookingQuote(null)}
      />
    </Box>
  );
}
